import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter,
  OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, computed, inject, input, viewChild,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UiDialogComponent } from '@template-ui/components';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzDialogData } from '@template-ui/types';

/** A destination supplied by the candidate's existing graph owner. */
export interface WorkflowReviewSetupDialogChoice { readonly id: string; readonly label: string; }

/** Read-only presentation of one installed review result and its candidate path. */
export interface WorkflowReviewSetupDialogOutcome {
  readonly code: string;
  readonly label: string;
  readonly movement: 'forward' | 'return';
  readonly destinationNodeId: string;
  readonly choices: readonly WorkflowReviewSetupDialogChoice[];
  readonly savedUnavailable?: string;
}

/** Actual effects and routes projected by the parent from its single staged candidate. */
export interface WorkflowReviewSetupDialogModel {
  readonly heading: string;
  readonly summary: readonly string[];
  readonly outcomes: readonly WorkflowReviewSetupDialogOutcome[];
  readonly canApply: boolean;
  readonly primaryLabel: string;
}

export interface WorkflowReviewSetupDialogRouteChange {
  readonly context: string;
  readonly code: string;
  readonly movement?: 'forward' | 'return';
  readonly destinationNodeId?: string;
}

export type WorkflowReviewSetupDialogClosedReason = 'dismissed' | 'input' | 'context' | 'inactive' | 'destroyed';
export interface WorkflowReviewSetupDialogClosed { readonly context: string; readonly reason: WorkflowReviewSetupDialogClosedReason; }

/** Presentation only: the owning editor validates and applies or discards every candidate. */
@Component({
  selector: 'phms-workflow-review-setup-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './workflow-review-setup-dialog.component.html',
  styleUrl: './workflow-review-setup-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowReviewSetupDialogComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static nextId = 0;
  readonly idPrefix = `workflow-review-setup-${++WorkflowReviewSetupDialogComponent.nextId}`;
  readonly open = input(false);
  readonly active = input(true);
  readonly context = input('');
  readonly disabled = input(false);
  readonly candidate = input<WorkflowReviewSetupDialogModel | null>(null);
  readonly error = input('');
  @Output() readonly routeChanged = new EventEmitter<WorkflowReviewSetupDialogRouteChange>();
  @Output() readonly applyRequested = new EventEmitter<{ context: string }>();
  @Output() readonly opened = new EventEmitter<{ context: string }>();
  @Output() readonly closed = new EventEmitter<WorkflowReviewSetupDialogClosed>();
  private readonly content = viewChild<TemplateRef<unknown>>('content');
  private readonly dialog = inject(MatDialog);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly rows = computed(() => this.candidate()?.outcomes ?? []);
  readonly applyAllowed = computed(() => !this.disabled() && !!this.candidate()?.canApply && this.rows().length > 0 && this.rows().every(row => !this.rowIssue(row)));
  private viewReady = false;
  private destroyed = false;
  private ref: MatDialogRef<UiDialogComponent> | null = null;
  private openedContext = '';
  private closeReason: WorkflowReviewSetupDialogClosedReason | null = null;
  private origin: HTMLElement | null = null;
  private focusReturnRequest = 0;
  private needsOpenReset = false;
  private restoringModel = false;

  ngAfterViewInit(): void { this.viewReady = true; this.syncDialog(); }
  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.open()) this.needsOpenReset = false;
    if (this.ref) {
      if (!this.active()) this.close('inactive');
      else if (this.context() !== this.openedContext || !this.candidate()) this.close('context');
      else if (!this.open()) this.close('input');
      if (this.ref && this.candidate()) this.ref.componentInstance.data.heading = this.candidate()!.heading;
    }
    this.syncDialog();
  }
  ngOnDestroy(): void {
    this.destroyed = true; this.focusReturnRequest++;
    const ref = this.ref;
    if (!ref) return;
    this.ref = null; this.origin = null; this.closeReason = 'destroyed'; ref.close();
    this.closed.emit({ context: this.openedContext, reason: 'destroyed' });
  }

  readonly trackOutcome = (index: number, row: WorkflowReviewSetupDialogOutcome) => this.rows().filter(item => item.code === row.code).length === 1 ? row.code : `${row.code}:${index}`;
  readonly trackChoice = (index: number, choice: WorkflowReviewSetupDialogChoice) => `${choice.id}:${index}`;
  hasUnavailableDestination(row: WorkflowReviewSetupDialogOutcome): boolean {
    return !!row.destinationNodeId && row.choices.filter(choice => choice.id === row.destinationNodeId).length !== 1;
  }
  rowIssue(row: WorkflowReviewSetupDialogOutcome): string {
    if (this.rows().filter(item => item.code === row.code).length !== 1) return 'This result identity is ambiguous. Reopen review setup before changing its paths.';
    if (this.hasUnavailableDestination(row)) return row.savedUnavailable || 'This saved destination is unavailable for the selected movement.';
    if (!row.destinationNodeId) return row.movement === 'return'
      ? row.choices.length ? 'Choose a work step for this return.' : 'No eligible earlier work step is available for this return.'
      : row.choices.length ? 'Choose a destination for this result.' : 'No eligible destination is available for this result.';
    return '';
  }
  changeMovement(row: WorkflowReviewSetupDialogOutcome, movement: string, model: NgModel): void {
    if (this.restoringModel) return;
    if (this.currentRow(row) && (movement === 'forward' || movement === 'return'))
      this.routeChanged.emit({ context: this.openedContext, code: row.code, movement });
    this.restoreModel(model, this.liveRow(row)?.movement ?? row.movement);
  }
  changeDestination(row: WorkflowReviewSetupDialogOutcome, destinationNodeId: string, model: NgModel): void {
    if (this.restoringModel) return;
    if (this.currentRow(row) && row.choices.filter(choice => choice.id === destinationNodeId).length === 1)
      this.routeChanged.emit({ context: this.openedContext, code: row.code, destinationNodeId });
    this.restoreModel(model, this.liveRow(row)?.destinationNodeId ?? row.destinationNodeId);
  }
  apply(): void { if (this.isCurrent() && this.applyAllowed()) this.applyRequested.emit({ context: this.openedContext }); }
  dismiss(): void { this.close(!this.active() ? 'inactive' : this.context() !== this.openedContext ? 'context' : 'dismissed'); }

  private liveRow(row: WorkflowReviewSetupDialogOutcome): WorkflowReviewSetupDialogOutcome | null {
    const matches = this.rows().filter(item => item.code === row.code);
    return matches.length === 1 ? matches[0] : null;
  }
  private currentRow(row: WorkflowReviewSetupDialogOutcome): boolean {
    return this.isCurrent() && !this.disabled() && this.liveRow(row) === row;
  }
  private restoreModel(model: NgModel, value: string): void {
    if (model.control.value === value && model.viewModel === value) return;
    this.restoringModel = true;
    try { model.control.setValue(value, { emitEvent: false }); } finally { this.restoringModel = false; }
  }
  private isCurrent(): boolean {
    return !!this.ref && !this.closeReason && !this.destroyed && this.open() && this.active() && !!this.candidate() && this.context() === this.openedContext;
  }
  private syncDialog(): void {
    const content = this.content(), candidate = this.candidate();
    if (!this.viewReady || this.destroyed || this.ref || !this.open() || !this.active() || !candidate || !content || this.needsOpenReset) return;
    this.focusReturnRequest++; this.closeReason = null; this.openedContext = this.context();
    const active = this.element.nativeElement.ownerDocument.activeElement;
    this.origin = active instanceof HTMLElement ? active : null;
    const ref = this.dialog.open(UiDialogComponent, {
      width: '48rem', maxWidth: 'calc(100vw - 2rem)', maxHeight: 'calc(100dvh - 2rem)',
      autoFocus: `#${this.idPrefix}-changes`, restoreFocus: false, disableClose: false,
      ariaLabel: candidate.heading,
      data: { heading: candidate.heading, buttons: [] } satisfies EzDialogData,
    });
    this.ref = ref; ref.componentRef?.setInput('contentTemplate', content);
    ref.beforeClosed().subscribe(() => { this.closeReason ??= 'dismissed'; this.needsOpenReset = this.open(); });
    ref.afterClosed().subscribe(() => {
      if (this.ref !== ref) return;
      const reason = this.closeReason ?? 'dismissed', context = this.openedContext, origin = this.origin;
      this.ref = null; this.origin = null; this.closed.emit({ context, reason });
      if (reason === 'dismissed' && origin) this.restoreDismissedFocus(origin, context);
    });
    this.opened.emit({ context: this.openedContext });
  }
  private close(reason: WorkflowReviewSetupDialogClosedReason): void {
    if (!this.ref || this.closeReason) return;
    this.closeReason = reason; this.needsOpenReset = this.open(); this.ref.close();
  }
  private restoreDismissedFocus(origin: HTMLElement, context: string): void {
    const request = ++this.focusReturnRequest;
    setTimeout(() => {
      const doc = origin.ownerDocument, active = doc.activeElement;
      if (request !== this.focusReturnRequest || this.destroyed || !this.active() || this.context() !== context || this.ref || this.dialog.openDialogs.length ||
        !origin.isConnected || origin.closest('[hidden],[inert]') || origin.matches(':disabled') ||
        active && active !== doc.body && active !== doc.documentElement && active !== origin && active.isConnected && !active.closest('[hidden],[inert]')) return;
      for (let node: HTMLElement | null = origin; node; node = node.parentElement) {
        const style = doc.defaultView?.getComputedStyle(node);
        if (style?.display === 'none' || style?.visibility === 'hidden' || style?.visibility === 'collapse') return;
      }
      origin.focus({ preventScroll: true });
    });
  }
}
